import * as React from 'react';

import Spinner from 'components/spinner/Spinner';
import TabsSection from 'components/tabs/tabs-section/TabsSection';

import OnsitePerformanceByCreatorTable from '../onsite-performance-by-creator-table/OnsitePerformanceByCreatorTable';
import OnsitePerformanceByTaskTable from '../onsite-performance-by-task-table/OnsitePerformanceByTaskTable';

import { isDefined } from 'utils/helper/common';
import { __t, applyTranslations } from 'utils/language';

import type { InfluencerCommon } from 'types';
import type { OnsitePerformanceData } from 'modules/reporting/types';

import './onsite-performance.scss';

applyTranslations(require('./onsite-performance.i18n.json'));

type MapByInfluencerId = {
  [x: number]: InfluencerCommon
};

const TABS = [
  {
    label: __t('onsite_performance.tabs.by_creator'),
    identifier: 'creator'
  },
  {
    label: __t('onsite_performance.tabs.by_task'),
    identifier: 'task'
  }
];

export const mapInfluencersById = (influencerProfiles: Array<InfluencerCommon>): MapByInfluencerId => {
  const influencersMapById = {};
  influencerProfiles?.forEach(profile => {
    influencersMapById[profile.id] = profile;
  });

  return influencersMapById;
};

type Props = {
  isLoading: boolean,
  hasData: boolean,
  onsitePerformance: OnsitePerformanceData
};

type State = {
  activeTab: string
};

class OnsitePerformance extends React.Component<Props, State> {
  state = {
    activeTab: 'creator'
  };

  handleTabClick = (activeTab: string) => {
    if (activeTab !== this.state.activeTab) {
      this.setState({
        activeTab
      });
    }
  };

  renderHeader(): React.ReactNode {
    const { activeTab } = this.state;

    return (
      <React.Fragment>
        <div className="onsite-performance__tabs">
          <TabsSection
            withShadow
            items={TABS}
            activeTab={activeTab}
            onClick={(tabId?: string | null) => this.handleTabClick((tabId || activeTab))}
          />
        </div>
        <div className="creator-performance-by-channel__title">
          {__t(`onsite_performance.tabs.by_${activeTab}`)}
        </div>
      </React.Fragment>
    );
  }

  renderContent(): React.ReactNode {
    const { onsitePerformance } = this.props;
    const { activeTab } = this.state;

    const influencersMapById = mapInfluencersById(onsitePerformance.influencerProfiles);

    if (activeTab === 'creator') {
      return (
        <OnsitePerformanceByCreatorTable
          influencerProfiles={onsitePerformance.influencerProfiles ?? []}
          influencersMapById={influencersMapById}
          performanceByCreator={onsitePerformance.performanceByCreator.filter(isDefined)}
        />
      );
    }

    if (activeTab === 'task') {
      return (
        <OnsitePerformanceByTaskTable
          influencerProfiles={onsitePerformance.influencerProfiles ?? []}
          influencersMapById={influencersMapById}
          performanceByTask={onsitePerformance.performanceByTask.filter(isDefined)}
        />
      );
    }

    return null;
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="onsite-performance__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  render() {
    const { isLoading, hasData } = this.props;

    if (isLoading) {
      return this.renderSpinner();
    }

    if (!hasData) {
      return null;
    }

    return (
      <div>
        {this.renderHeader()}
        {this.renderContent()}
      </div>
    );
  }
}

export default OnsitePerformance;
